import { useLanguage } from "./useLanguage";
import { getTranslatedUrl, getRouteKeyFromPath, allRoutesComplete } from "@/lib/route-mapping";
import { useMemo } from "react";

/**
 * Hook to get localized URL for internal links
 * Automatically translates URLs based on current language
 * 
 * @example
 * const link = useLocalizedLink('/practice-areas/immigration-services');
 * // Returns: '/es/areas-de-practica/servicios-de-inmigracion' if language is 'es'
 */
export function useLocalizedLink(path: string): string {
  const { language } = useLanguage();
  
  return useMemo(() => {
    // Try to find route key from the English path
    const routeKey = getRouteKeyFromPath(path);
    
    if (routeKey) {
      // Found in mapping, use translated URL
      return getTranslatedUrl(routeKey, language);
    }
    
    // Not in mapping yet, return path as-is
    // This handles pages not yet in route mapping (areas, blog, etc.)
    return path;
  }, [path, language]);
}

/**
 * Hook to generate localized links for multiple paths
 * Useful for navigation menus
 */
export function useLocalizedLinks(paths: string[]): string[] {
  const { language } = useLanguage();
  
  return useMemo(() => {
    return paths.map(path => {
      const routeKey = getRouteKeyFromPath(path);
      return routeKey ? getTranslatedUrl(routeKey, language) : path;
    });
  }, [paths, language]);
}

/**
 * Get all route keys for easier discovery
 */
export function getAllRouteKeys(): string[] {
  return Object.keys(allRoutesComplete);
}
